<script setup>
import { computed } from 'vue';
import ChannelSelector from '../ChannelSelector.vue';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
  enabledModules: {
    type: Object,
    default: () => ({}),
  },
  limitReached: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['channelItemClick']);

const hasFbConfigured = computed(() => {
  return window.lumeratecConfig?.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  return window.lumeratecConfig?.instagramAppId;
});

const hasTiktokConfigured = computed(() => {
  return window.lumeratecConfig?.tiktokAppId;
});

const isActive = computed(() => {
  const { key } = props.channel;

  if (key === 'website') {
    return props.enabledFeatures?.channel_website;
  }
  if (key === 'facebook') {
    return props.enabledFeatures?.channel_facebook && hasFbConfigured.value;
  }
  if (key === 'email') {
    return props.enabledFeatures?.channel_email;
  }
  if (key === 'instagram') {
    return (
      props.enabledFeatures?.channel_instagram && hasInstagramConfigured.value
    );
  }
  if (key === 'tiktok') {
    return props.enabledFeatures?.channel_tiktok && hasTiktokConfigured.value;
  }
  if (key === 'mercado_livre') {
    return false;
  }
  if (key === 'voice') {
    return props.enabledFeatures?.channel_voice;
  }
  if (key === 'whatsapp_call') {
    return (
      props.enabledFeatures?.channel_voice &&
      !!window.lumeratecConfig?.whatsappAppId &&
      window.lumeratecConfig.whatsappAppId !== 'none'
    );
  }

  // Lumeratec modules check (from plan OR from purchased add-ons)
  if (key === 'whatsapp') {
    return (
      props.enabledModules?.inbox_whatsapp === true ||
      props.enabledModules?.inbox_whatsapp === 'true' ||
      props.enabledFeatures?.inbox_whatsapp
    );
  }
  if (key === 'telegram') {
    return false;
  }
  if (key === 'line') {
    return (
      props.enabledModules?.inbox_line === true ||
      props.enabledModules?.inbox_line === 'true' ||
      props.enabledFeatures?.inbox_line
    );
  }
  if (key === 'api') {
    return (
      props.enabledModules?.api === true ||
      props.enabledModules?.api === 'true' ||
      props.enabledFeatures?.api
    );
  }

  return key === 'sms';
});

const isComingSoon = computed(() => {
  const { key } = props.channel;
  // Show "Coming Soon" only if the channel is marked as coming soon
  // and the corresponding feature flag is not enabled yet.
  return ['voice'].includes(key) && !isActive.value;
});

const isBeta = computed(() => {
  return ['telegram', 'tiktok', 'mercado_livre'].includes(props.channel.key);
});

const hasVoiceBadge = computed(() => {
  return (
    ['voice', 'whatsapp_call'].includes(props.channel.key) &&
    !!props.enabledFeatures?.channel_voice
  );
});

const onItemClick = () => {
  if (isActive.value && !props.limitReached) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :is-beta="isBeta"
    :has-voice-badge="hasVoiceBadge"
    :limit-reached="limitReached"
    :disabled="!isActive || limitReached"
    @click="onItemClick"
  />
</template>
